import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ListTodo, Loader2 } from "lucide-react";
import { getProcessingTasks } from "../../rules/ruleService";
import {
    formatTaskDate,
    getTaskStartTime,
    getTaskStatus,
    getTaskTitle,
    getTaskType,
    hasTaskStartTime,
    isProcessingTask,
    mergeRefreshedTasks
} from "../../processing-tasks/processingTasksUtils";
import {
    cacheProcessingTasks,
    getCachedProcessingTasks,
    getLocalProcessingTasks,
    PROCESSING_TASKS_CHANGED_EVENT
} from "../../processing-tasks/processingTasksRegistry";

function updateTaskRow(tasks, updatedTask) {
    if (!updatedTask?.id) {
        return tasks;
    }

    const existingIndex = tasks.findIndex(task => task.id === updatedTask.id);
    if (existingIndex === -1) {
        return [updatedTask, ...tasks];
    }

    return tasks.map(task => task.id === updatedTask.id ? {
        ...task,
        ...updatedTask
    } : task);
}

export function ProcessingTasksLink() {
    const history = useHistory();
    const dropdownRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [tasks, setTasks] = useState([]);

    const loadTasks = async () => {
        try {
            const response = await getProcessingTasks();
            const serverTasks = response?.page || [];
            cacheProcessingTasks(serverTasks);
            const refreshedTasks = [...serverTasks, ...getCachedProcessingTasks(), ...getLocalProcessingTasks()];
            setTasks(currentTasks => {
                const mergedTasks = mergeRefreshedTasks(currentTasks, refreshedTasks);
                cacheProcessingTasks(mergedTasks);
                return mergedTasks;
            });
        } catch (error) {
            const refreshedTasks = [...getCachedProcessingTasks(), ...getLocalProcessingTasks()];
            setTasks(currentTasks => {
                const mergedTasks = mergeRefreshedTasks(currentTasks, refreshedTasks);
                cacheProcessingTasks(mergedTasks);
                return mergedTasks;
            });
        }
    };

    useEffect(() => {
        loadTasks();
        const onProcessingTaskChanged = event => {
            const updatedTask = event?.detail?.task;
            if (updatedTask) {
                setTasks(currentTasks => updateTaskRow(currentTasks, updatedTask));
                return;
            }

            loadTasks();
        };

        window.addEventListener(PROCESSING_TASKS_CHANGED_EVENT, onProcessingTaskChanged);
        window.addEventListener("storage", loadTasks);
        return () => {
            window.removeEventListener(PROCESSING_TASKS_CHANGED_EVENT, onProcessingTaskChanged);
            window.removeEventListener("storage", loadTasks);
        };
    }, []);

    useEffect(() => {
        const onDocumentClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", onDocumentClick);
        return () => document.removeEventListener("mousedown", onDocumentClick);
    }, []);

    const goToProcessingTasks = (taskId) => {
        setOpen(false);
        history.push(taskId ? `/processing-tasks?task=${taskId}` : "/processing-tasks");
    };

    const validTasks = tasks.filter(hasTaskStartTime);
    const sortedTasks = validTasks.filter(isProcessingTask).sort((left, right) =>
        Number(right.completedTime || getTaskStartTime(right) || 0) - Number(left.completedTime || getTaskStartTime(left) || 0)
    );
    const visibleTasks = sortedTasks.slice(0, 5);
    const processingCount = validTasks.filter(isProcessingTask).length;

    return (
        <div className="processing-tasks-header" ref={dropdownRef}>
            <button
                className="icon-btn processing-icon-btn"
                onClick={() => setOpen(!open)}
                aria-label="Processing tasks">
                {processingCount > 0 ? (
                    <Loader2 className="btn-icon processing-spin" />
                ) : (
                    <ListTodo className="btn-icon" />
                )}
            </button>

            {open && (
                <div className="processing-tasks-dropdown">
                    <div className="processing-tasks-dropdown-header">
                        <div>
                            <div className="processing-tasks-title">
                                Processing Tasks
                            </div>
                            <div className="processing-tasks-subtitle">
                                {processingCount} running now
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => goToProcessingTasks()}>
                            View all
                        </button>
                    </div>

                    <div className="processing-tasks-list">
                        {visibleTasks.length ? (
                            visibleTasks.map((task) => (
                                <button
                                    type="button"
                                    className="processing-task-item"
                                    key={task.id}
                                    onClick={() => goToProcessingTasks(task.id)}>
                                    <span
                                        className={`processing-task-dot is-${getTaskStatus(task).toLowerCase()}`}
                                    />

                                    <span className="processing-task-content">
                                        <strong>{getTaskTitle(task)}</strong>
                                        <span>
                                            {task.device || task.target || "-"} /{" "}
                                            {task.msb || "-"} /{" "}
                                            {getTaskType(task)}
                                        </span>
                                        <small>
                                            {getTaskStatus(task)} ·{" "}
                                            {formatTaskDate(getTaskStartTime(task))}
                                        </small>
                                    </span>
                                </button>
                            ))
                        ) : (
                            <div className="processing-tasks-empty">
                                No processing tasks right now.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
